import React, { useState, useEffect, useMemo } from 'react';
import { BarChart3, TrendingUp, Clock, Users, Calendar, Download, FileText } from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line, Bar, Pie, Doughnut } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

export default function Stats({ fiches, darkMode }) {
  const [periodFilter, setPeriodFilter] = useState('month'); // day, week, month, year, custom
  const [clientFilter, setClientFilter] = useState('all');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  
  const bg = darkMode ? 'bg-slate-950' : 'bg-white';
  const bgAlt = darkMode ? 'bg-slate-900' : 'bg-slate-50';
  const text = darkMode ? 'text-slate-50' : 'text-slate-900';
  const textMuted = darkMode ? 'text-slate-400' : 'text-slate-600';
  const border = darkMode ? 'border-slate-800' : 'border-slate-200';

  // Filtrer les fiches selon les critères
  const filteredFiches = useMemo(() => {
    let filtered = fiches.filter(f => f.date && f.date !== 'Non précisée');
    
    // Filtre par client
    if (clientFilter !== 'all') {
      filtered = filtered.filter(f => f.client === clientFilter);
    }
    
    // Filtre par période
    const now = new Date();
    filtered = filtered.filter(f => {
      const [day, month, year] = f.date.split('/');
      const ficheDate = new Date(year, month - 1, day);
      
      switch(periodFilter) {
        case 'day':
          return ficheDate.toDateString() === now.toDateString();
        case 'week':
          const weekAgo = new Date(now);
          weekAgo.setDate(weekAgo.getDate() - 7);
          return ficheDate >= weekAgo;
        case 'month':
          const monthAgo = new Date(now);
          monthAgo.setMonth(monthAgo.getMonth() - 1);
          return ficheDate >= monthAgo;
        case 'year':
          const yearAgo = new Date(now);
          yearAgo.setFullYear(yearAgo.getFullYear() - 1);
          return ficheDate >= yearAgo;
        case 'custom':
          if (dateRange.start && dateRange.end) {
            const startDate = new Date(dateRange.start);
            const endDate = new Date(dateRange.end);
            return ficheDate >= startDate && ficheDate <= endDate;
          }
          return true;
        default:
          return true;
      }
    });
    
    return filtered;
  }, [fiches, periodFilter, clientFilter, dateRange]);

  // Calculer les statistiques globales
  const stats = useMemo(() => {
    const total = filteredFiches.length;
    
    // Calculer la durée moyenne
    const durations = filteredFiches.map(f => {
      if (!f.duree || f.duree === '—') return 0;
      const match = f.duree.match(/(\d+)m(\d+)s/);
      if (match) {
        return parseInt(match[1]) + parseInt(match[2]) / 60;
      }
      return 0;
    });
    const avgDuration = durations.length > 0 
      ? Math.round(durations.reduce((a, b) => a + b, 0) / durations.length) 
      : 0;
    
    const validated = filteredFiches.filter(f => f.statut === 'Validée').length;
    const nouveau = filteredFiches.filter(f => f.statut === 'Nouveau').length;
    
    // Clients uniques
    const uniqueClients = new Set(filteredFiches.map(f => f.client)).size;
    
    return { total, avgDuration, validated, nouveau, uniqueClients };
  }, [filteredFiches]);

  // Données pour le graphique d'évolution temporelle
  const timelineData = useMemo(() => {
    const grouped = {};
    
    filteredFiches.forEach(f => {
      const [day, month, year] = f.date.split('/');
      let key;
      
      if (periodFilter === 'day') {
        key = `${day}/${month}`;
      } else if (periodFilter === 'week' || periodFilter === 'month') {
        key = `${day}/${month}`;
      } else {
        key = `${month}/${year}`;
      }
      
      grouped[key] = (grouped[key] || 0) + 1;
    });
    
    const sorted = Object.entries(grouped).sort((a, b) => {
      const [dayA, monthA] = a[0].split('/');
      const [dayB, monthB] = b[0].split('/');
      return new Date(2025, monthA - 1, dayA) - new Date(2025, monthB - 1, dayB);
    });
    
    return {
      labels: sorted.map(([key]) => key),
      datasets: [{
        label: 'Nombre d\'interventions',
        data: sorted.map(([, count]) => count),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.4,
        fill: true
      }]
    };
  }, [filteredFiches, periodFilter]);

  // Données pour la répartition par statut
  const statusData = useMemo(() => {
    const validated = filteredFiches.filter(f => f.statut === 'Validée').length;
    const nouveau = filteredFiches.filter(f => f.statut === 'Nouveau').length;
    
    return {
      labels: ['Validées', 'Nouvelles'],
      datasets: [{
        data: [validated, nouveau],
        backgroundColor: [
          'rgba(34, 197, 94, 0.8)',
          'rgba(251, 146, 60, 0.8)'
        ],
        borderColor: [
          'rgb(34, 197, 94)',
          'rgb(251, 146, 60)'
        ],
        borderWidth: 2
      }]
    };
  }, [filteredFiches]);

  // Données pour les clients les plus fréquents
  const clientsData = useMemo(() => {
    const clientCounts = {};
    filteredFiches.forEach(f => {
      clientCounts[f.client] = (clientCounts[f.client] || 0) + 1;
    });
    
    const sorted = Object.entries(clientCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10);
    
    return {
      labels: sorted.map(([client]) => client),
      datasets: [{
        label: 'Nombre d\'interventions',
        data: sorted.map(([, count]) => count),
        backgroundColor: 'rgba(59, 130, 246, 0.8)',
        borderColor: 'rgb(59, 130, 246)',
        borderWidth: 2
      }]
    };
  }, [filteredFiches]);

  // Durée moyenne par période
  const avgDurationData = useMemo(() => {
    const grouped = {};
    
    filteredFiches.forEach(f => {
      const [day, month, year] = f.date.split('/');
      const key = periodFilter === 'year' ? `${month}/${year}` : `${day}/${month}`;
      
      if (!grouped[key]) grouped[key] = { total: 0, count: 0 };
      
      if (f.duree && f.duree !== '—') {
        const match = f.duree.match(/(\d+)m(\d+)s/);
        if (match) {
          const minutes = parseInt(match[1]) + parseInt(match[2]) / 60;
          grouped[key].total += minutes;
          grouped[key].count += 1;
        }
      }
    });
    
    const sorted = Object.entries(grouped)
      .map(([key, val]) => [key, val.count > 0 ? val.total / val.count : 0])
      .sort((a, b) => {
        const [dayA, monthA] = a[0].split('/');
        const [dayB, monthB] = b[0].split('/');
        return new Date(2025, monthA - 1, dayA) - new Date(2025, monthB - 1, dayB);
      });
    
    return {
      labels: sorted.map(([key]) => key),
      datasets: [{
        label: 'Durée moyenne (minutes)',
        data: sorted.map(([, avg]) => Math.round(avg)),
        borderColor: 'rgb(168, 85, 247)',
        backgroundColor: 'rgba(168, 85, 247, 0.1)',
        tension: 0.4,
        fill: true
      }]
    };
  }, [filteredFiches, periodFilter]);

  // Liste unique des clients pour le filtre
  const uniqueClients = useMemo(() => {
    return [...new Set(fiches.map(f => f.client))].sort();
  }, [fiches]);

  // Export CSV
  const exportCSV = () => {
    const headers = ['Date', 'Heure', 'Client', 'Durée', 'Statut', 'Sujet'];
    const rows = filteredFiches.map(f => [
      f.date,
      f.heure,
      f.client,
      f.duree,
      f.statut,
      f.sujet.replace(/\n/g, ' ')
    ]);
    
    const csvContent = [
      headers.join(';'),
      ...rows.map(row => row.join(';'))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `statistiques_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: {
          color: darkMode ? '#cbd5e1' : '#334155'
        }
      }
    },
    scales: {
      x: {
        ticks: { color: darkMode ? '#cbd5e1' : '#334155' },
        grid: { color: darkMode ? '#334155' : '#e2e8f0' }
      },
      y: {
        ticks: { color: darkMode ? '#cbd5e1' : '#334155' },
        grid: { color: darkMode ? '#334155' : '#e2e8f0' }
      }
    }
  };

  return (
    <div className="flex-1 p-8 overflow-y-auto">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Statistiques</h1>
            <p className={`${textMuted} mt-1`}>Analyse et visualisation des interventions</p>
          </div>
          <button onClick={exportCSV} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition">
            <Download size={18} />
            Exporter CSV
          </button>
        </div>

        {/* Filtres */}
        <div className={`${bgAlt} rounded-lg p-6 border ${border}`}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className={`block text-sm font-medium mb-2 ${textMuted}`}>Période</label>
              <select 
                value={periodFilter} 
                onChange={(e) => setPeriodFilter(e.target.value)}
                className={`w-full px-4 py-2 rounded-lg border ${border} ${bg} ${text}`}>
                <option value="day">Aujourd'hui</option>
                <option value="week">7 derniers jours</option>
                <option value="month">30 derniers jours</option>
                <option value="year">Cette année</option>
                <option value="all">Tout</option>
              </select>
            </div>
            
            <div>
              <label className={`block text-sm font-medium mb-2 ${textMuted}`}>Client</label>
              <select 
                value={clientFilter} 
                onChange={(e) => setClientFilter(e.target.value)}
                className={`w-full px-4 py-2 rounded-lg border ${border} ${bg} ${text}`}>
                <option value="all">Tous les clients</option>
                {uniqueClients.map(client => (
                  <option key={client} value={client}>{client}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className={`block text-sm font-medium mb-2 ${textMuted}`}>Affichage</label>
              <div className={`px-4 py-2 rounded-lg border ${border} ${bgAlt} text-sm`}>
                {filteredFiches.length} fiche(s) affichée(s)
              </div>
            </div>
          </div>
        </div>

        {/* Cards statistiques */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className={`${bgAlt} rounded-lg p-6 border ${border}`}>
            <div className="flex items-center justify-between mb-2">
              <div className={`p-3 rounded-lg bg-blue-900/30`}>
                <FileText size={24} className="text-blue-400" />
              </div>
            </div>
            <div className="text-3xl font-bold mb-1">{stats.total}</div>
            <div className={`text-sm ${textMuted}`}>Total interventions</div>
          </div>

          <div className={`${bgAlt} rounded-lg p-6 border ${border}`}>
            <div className="flex items-center justify-between mb-2">
              <div className={`p-3 rounded-lg bg-purple-900/30`}>
                <Clock size={24} className="text-purple-400" />
              </div>
            </div>
            <div className="text-3xl font-bold mb-1">{stats.avgDuration}min</div>
            <div className={`text-sm ${textMuted}`}>Durée moyenne</div>
          </div>

          <div className={`${bgAlt} rounded-lg p-6 border ${border}`}>
            <div className="flex items-center justify-between mb-2">
              <div className={`p-3 rounded-lg bg-green-900/30`}>
                <TrendingUp size={24} className="text-green-400" />
              </div>
            </div>
            <div className="text-3xl font-bold mb-1">{stats.validated}</div>
            <div className={`text-sm ${textMuted}`}>Fiches validées</div>
          </div>

          <div className={`${bgAlt} rounded-lg p-6 border ${border}`}>
            <div className="flex items-center justify-between mb-2">
              <div className={`p-3 rounded-lg bg-orange-900/30`}>
                <Users size={24} className="text-orange-400" />
              </div>
            </div>
            <div className="text-3xl font-bold mb-1">{stats.uniqueClients}</div>
            <div className={`text-sm ${textMuted}`}>Clients uniques</div>
          </div>
        </div>

        {/* Graphiques */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Évolution temporelle */}
          <div className={`${bgAlt} rounded-lg p-6 border ${border}`}>
            <h3 className="text-lg font-semibold mb-4">Évolution des interventions</h3>
            <div className="h-64">
              <Line data={timelineData} options={chartOptions} />
            </div>
          </div>

          {/* Répartition par statut */}
          <div className={`${bgAlt} rounded-lg p-6 border ${border}`}>
            <h3 className="text-lg font-semibold mb-4">Répartition par statut</h3>
            <div className="h-64 flex items-center justify-center">
              <Doughnut data={statusData} options={{ ...chartOptions, scales: undefined }} />
            </div>
          </div>

          {/* Top clients */}
          <div className={`${bgAlt} rounded-lg p-6 border ${border}`}>
            <h3 className="text-lg font-semibold mb-4">Top 10 clients</h3>
            <div className="h-64">
              <Bar data={clientsData} options={chartOptions} />
            </div>
          </div>

          {/* Durée moyenne */}
          <div className={`${bgAlt} rounded-lg p-6 border ${border}`}>
            <h3 className="text-lg font-semibold mb-4">Durée moyenne d'intervention</h3>
            <div className="h-64">
              <Line data={avgDurationData} options={chartOptions} />
            </div>
          </div>
        </div>

        {/* Tableau récapitulatif */}
        <div className={`${bgAlt} rounded-lg p-6 border ${border}`}>
          <h3 className="text-lg font-semibold mb-4">Dernières interventions</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className={`border-b ${border}`}>
                  <th className={`text-left p-3 ${textMuted} font-medium`}>Date</th>
                  <th className={`text-left p-3 ${textMuted} font-medium`}>Client</th>
                  <th className={`text-left p-3 ${textMuted} font-medium`}>Durée</th>
                  <th className={`text-left p-3 ${textMuted} font-medium`}>Statut</th>
                  <th className={`text-left p-3 ${textMuted} font-medium`}>Sujet</th>
                </tr>
              </thead>
              <tbody>
                {filteredFiches.slice(0, 10).map((f, i) => (
                  <tr key={i} className={`border-b ${border} hover:bg-slate-800/30 transition`}>
                    <td className="p-3 text-sm">{f.date}</td>
                    <td className="p-3 text-sm font-medium">{f.client}</td>
                    <td className="p-3 text-sm">{f.duree}</td>
                    <td className="p-3">
                      <span className={`text-xs px-2 py-1 rounded-full ${f.statut === 'Validée' ? 'bg-green-900 text-green-200' : 'bg-orange-900 text-orange-200'}`}>
                        {f.statut}
                      </span>
                    </td>
                    <td className="p-3 text-sm line-clamp-1">{f.sujet}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}